import { useRef } from 'react';

export type ApiQueryInvalidationFilters = {
  includeUniqueQueryKeys?: readonly string[];
  excludeUniqueQueryKeys?: readonly string[];
};

export function areArraysShallowEqual<T>(
  previous: readonly T[] | undefined,
  next: readonly T[] | undefined
) {
  if (previous === next) return true;
  if (!previous || !next) return previous === next;
  if (previous.length !== next.length) return false;

  return previous.every((value, index) => Object.is(value, next[index]));
}

export function getShallowStableArray<T>(
  previous: readonly T[] | undefined,
  next: readonly T[] | undefined
) {
  return areArraysShallowEqual(previous, next) ? previous : next;
}

export function useShallowStableArray<T>(value: readonly T[] | undefined) {
  const ref = useRef(value);

  ref.current = getShallowStableArray(ref.current, value);

  return ref.current;
}

export function getApiQueryUniqueQueryKey(queryKey: readonly unknown[]) {
  const uniqueQueryKey = queryKey[1];

  return typeof uniqueQueryKey === 'string' ? uniqueQueryKey : undefined;
}

export function matchesApiQueryInvalidation(
  queryKey: readonly unknown[],
  filters?: ApiQueryInvalidationFilters
) {
  const uniqueQueryKey = getApiQueryUniqueQueryKey(queryKey);
  const includeUniqueQueryKeys = filters?.includeUniqueQueryKeys ?? [];
  const excludeUniqueQueryKeys = filters?.excludeUniqueQueryKeys ?? [];

  if (
    includeUniqueQueryKeys.length > 0 &&
    (!uniqueQueryKey || !includeUniqueQueryKeys.includes(uniqueQueryKey))
  ) {
    return false;
  }

  if (
    uniqueQueryKey &&
    excludeUniqueQueryKeys.length > 0 &&
    excludeUniqueQueryKeys.includes(uniqueQueryKey)
  ) {
    return false;
  }

  return true;
}

export function matchesApiQueryTenantScope(
  meta: Readonly<Record<string, unknown>> | undefined,
  activeTeamId: string | null | undefined
) {
  if (meta?.teamScoped === false) return true;

  return (
    meta?.teamScoped === true &&
    typeof activeTeamId === 'string' &&
    meta.teamId === activeTeamId
  );
}
